import { Data } from "@core/content/types";
import { Vector3 } from "babylonjs";

import LeatherbackModel from '../assets/content/LeatherBack.glb?url';
import JellyfishModel from '../assets/content/LeatherBack-Jellyfish.glb?url';
import GreenSeaTurtleModel from '../assets/content/GreenSeaTurtle.glb?url';
import LoggerheadTurtleModel from '../assets/content/LoggerheadTurtle.glb?url';
import FlatbackTurtleModel from '../assets/content/FlatbackTurtle.glb?url';
import HawksbillTurtleModel from '../assets/content/HawksbillTurtle.glb?url';
import OliveRidleyTurtleModel from '../assets/content/OliveRidleyTurtle.glb?url';

import ButchullaPanels from '../assets/content/panels/ButchullaPanels.glb?url';

// Shared turtle preview environment (supports image and .env paths)
import BlueWaterEnvironment from '../assets/environments/leatherback-turtle-mirrored-10.jpg';
import GreenTurtleEnvironment from '../assets/environments/green-turtle-mirrored-10.jpg?url';
import LoggerheadTurtleEnvironment from '../assets/environments/loggerhead-turtle-15.jpg?url';
import FlatbackTurtleEnvironment from '../assets/environments/flatback-turtle-mirrored-15.jpg?url';
import HawksbillTurtleEnvironment from '../assets/environments/hawksbill-turtle-mirrored-10.jpg?url';
import OliveRidleyTurtleEnvironment from '../assets/environments/olive-ridley-turtle-mirrored-10.jpg?url';
import SharedEnvLighting from '../assets/content/sanGiuseppeBridge.env?url';
import LeatherbackQrCode from '../assets/qr-codes/leatherback-turtle.svg';
import GreenQrCode from '../assets/qr-codes/green-turtle.svg';
import LoggerheadQrCode from '../assets/qr-codes/loggerhead-turtle.svg';
import FlatbackQrCode from '../assets/qr-codes/flatback-turtle.svg';
import HawksbillQrCode from '../assets/qr-codes/hawksbill-turtle.svg';
import OliveRidleyQrCode from '../assets/qr-codes/olive-ridley-turtle.svg';
import ButchullaQrCode from '../assets/qr-codes/butchulla-people.svg';

import GreenTurtleVideo from "../assets/turtle-images/green-sea-turtle-2.mp4?url";
import HawksbillTurtleVideo from "../assets/turtle-images/hawksbill-turtle-2.mp4?url";
import ButchullaVideo from "../assets/microsite/butchulla-lores-opt.mp4?url";
// ============================================================================
// AR TYPE DEFINITION
// ============================================================================
export type ARType = 'webxr';

// Content section for turtle details
export interface TurtleContentSection {
  title: string;
  description?: string;
  bullets?: string[];
}

// AR gesture options that can be set per-content (matches GestureOptions from boilerplate)
export interface ARGestureOptions {
  /** Minimum scale (default 0.5) */
  minScale?: number;
  /** Maximum scale (default 2.0) */
  maxScale?: number;
  /** Ground Y position for pan limits */
  groundY?: number;
  /** Minimum distance from camera */
  minDist?: number;
  /** Maximum distance from camera */
  maxDist?: number;
  /** Disable scaling (pinch gesture) */
  disableScale?: boolean;
  /** Disable rotation (one-finger drag) */
  disableRotation?: boolean;
}

// Extended data type with UI fields
export interface ModelData extends Data {
  // URL hash fragment used for in-page anchors (without leading '#')
  urlHash?: string;
  // UI display fields (prefixed to avoid conflicts with Data type)
  displayName?: string;
  displaySubtitle?: string;
  tagline?: string;
  poster?: string;
  dark?: boolean;
  arType: ARType;
  // Turtle-specific content
  scientificName?: string;
  conservationStatus?: string;
  facts?: string[];
  contentSections?: TurtleContentSection[];
  // Viewer rotation (Y-axis in degrees) - for adjusting model facing direction
  viewerRotationY?: number;
  // Viewer camera offset { x, y } - shifts where camera looks (positive y = up, positive x = right)
  viewerOffset?: { x: number; y: number };
  // Preview environment for model viewer canvas (.env or image path)
  environment?: string;
  // Separate IBL lighting environment (.env file) - if set, used for lighting while environment is used for skybox
  iblEnvironment?: string;
  // Flag to exclude from turtle sections list
  excludeFromTurtleSections?: boolean;
  // Preview: exclude meshes whose names match any of these patterns (case-insensitive)
  excludeMeshes?: string[];
  // Preview: when set, show ONLY meshes whose names match these (nothing else).
  includeMeshes?: string[];
  // AR gesture options for this content
  arGestureOptions?: ARGestureOptions;
  // QR code asset used in the desktop popup
  qrCodePath?: string;
  // Video texture to apply to a mesh named "VideoPlane" in the model
  videoTexturePath?: string;
}

/**
 * Single source of truth for all model data
 * Used by both ContentInstanceManager and UI components
 */
export const ContentData: ModelData[] = [
  
  {
    id: "leatherback",
    urlHash: "leatherback-turtle",
    type: "scene",
    name: "Leatherback Turtle",
    content: [
      {
        type: "model",
        path: LeatherbackModel,
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(1.08, 1.08, 1.08), // 1.83m head-to-tail

      },
      {
        type: "model",
        path: JellyfishModel,
        rotation: new Vector3(0, -Math.PI / 2, 0),

      }
    ],
    includeMeshes: ['Leatherback_mesh',], // Or show only these meshes
    viewerOffset: { x: 0, y: -0.06 },  
    viewerRotationY: -30,
    environment: BlueWaterEnvironment,
    iblEnvironment: SharedEnvLighting,
    // UI fields
    displayName: "Leatherback",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: LeatherbackQrCode,
    scientificName: "Dermochelys coriacea",
    
  },
  {
    id: "green",
    urlHash: "green-turtle",
    type: "scene",
    name: "Green Turtle",
    content: [
      {
        type: "model",
        path: GreenSeaTurtleModel,
        // position: new Vector3(0, 1, 0),
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(1.8961, 1.8961, 1.8961),
      },
    ],
    environment: GreenTurtleEnvironment,
    iblEnvironment: SharedEnvLighting,
    includeMeshes: ['GreenSea_mesh'],
    viewerRotationY: -30,
    videoTexturePath: GreenTurtleVideo,
    // UI fields
    displayName: "Green",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: GreenQrCode,
    scientificName: "Chelonia mydas",
    
  },
  {
    id: "loggerhead",
    urlHash: "loggerhead-turtle",
    type: "scene",
    name: "Loggerhead Turtle",
    content: [
      {
        type: "model",
        path: LoggerheadTurtleModel,
        // position: new Vector3(0, 1, 0),
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(1.2762, 1.2762, 1.2762),
      },
    ],
    environment: LoggerheadTurtleEnvironment,
    iblEnvironment: SharedEnvLighting,
    viewerRotationY: -30,
    includeMeshes: ['Object_37001'],
    // UI fields
    displayName: "Loggerhead",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: LoggerheadQrCode,
    scientificName: "Caretta caretta",
    // viewerRotationY: 80, // Rotate to face camera
    
  },
  {
    id: "flatback",
    urlHash: "flatback-turtle",
    type: "scene",
    name: "Flatback Turtle",
    content: [
      {
        type: "model",
        path: FlatbackTurtleModel,
        // position: new Vector3(0, 1, 0),
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(0.8909, 0.8909, 0.8909), // 0.98m length 
      },
    ],
    environment: FlatbackTurtleEnvironment,
    iblEnvironment: SharedEnvLighting,
    viewerRotationY: -30,
    includeMeshes: ['Flatback_mesh'],
    // UI fields
    displayName: "Flatback",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: FlatbackQrCode,
    scientificName: "Natator depressus",
    
  },
  {
    id: "hawksbill",
    urlHash: "hawksbill-turtle",
    type: "scene",
    name: "Hawksbill Turtle",
    content: [
      {
        type: "model",
        path: HawksbillTurtleModel,
        // position: new Vector3(0, 1, 0),
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(1.1877, 1.1877, 1.1877),
      },
    ],
    environment: HawksbillTurtleEnvironment,
    iblEnvironment: SharedEnvLighting,
    viewerOffset: { x: 0, y: -0.08 },  
    viewerRotationY: -25,
    includeMeshes: ['hawksbill_primitive'],
    // UI fields
    displayName: "Hawksbill",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: HawksbillQrCode,
    scientificName: "Eretmochelys imbricata",
    videoTexturePath: HawksbillTurtleVideo,
  },
  {
    id: "olive-ridley",
    urlHash: "olive-ridley-turtle",
    type: "scene",
    name: "Olive Ridley Turtle",
    content: [
      {
        type: "model",
        path: OliveRidleyTurtleModel,
        rotation: new Vector3(0, -Math.PI / 2, 0),
        // scale: new Vector3(0.9506, 0.9506, 0.9506),
      },
    ],
    environment: OliveRidleyTurtleEnvironment,
    iblEnvironment: SharedEnvLighting,
    viewerOffset: { x: 0.1, y: -0.06 }, 
    includeMeshes: ['oliveridley_opt'],
    viewerRotationY: -54,
    // UI fields
    displayName: "Olive Ridley",
    displaySubtitle: "Turtle",
    arType: 'webxr',
    qrCodePath: OliveRidleyQrCode,
    scientificName: "Lepidochelys olivacea",
    
  },
  {
    id: "butchulla",
    urlHash: "butchulla-people",
    type: "scene",
    name: "Butchulla",
    content: [
      {
        type: "model",
        path: ButchullaPanels,
        // path: "https://launch.eyejackapp.com/Artwork-6b848978-a366-471e-9f22-06089261e63c?j=1"
        // position: new Vector3(0, 0, -2),
        // rotation: new Vector3(0, -Math.PI / 2, 0),

        // arGestureOptions: { disableRotation: true },
      }
    ],
    // UI fields
    displayName: "Butchulla",
    displaySubtitle: "Milbi Legacy",
    arType: 'webxr',
    qrCodePath: ButchullaQrCode,
    videoTexturePath: ButchullaVideo,
  },

];

/**
 * Per-turtle mesh scale values (applied only to included turtle meshes).
 * Mesh name matching uses the same patterns from each content item's `includeMeshes`.
 */
export const TURTLE_MESH_SCALE_BY_ID: Partial<Record<string, number>> = {
  leatherback: 1.08,
  green: 1.8961,
  loggerhead: 1.2762,
  flatback: 0.8909,
  hawksbill: 1.1877,
  "olive-ridley": 0.9506,
};

/**
 * Optional per-turtle whole-model overrides.
 * Add or uncomment ids here when you want to scale the full model (not just included meshes).
 */
export const TURTLE_MODEL_SCALE_BY_ID: Partial<Record<string, number>> = {
  // leatherback: 1.08,
  // green: 1.8961,
  // loggerhead: 1.2762,
  // flatback: 0.8909,
  // hawksbill: 1.1877,
  // "olive-ridley": 0.9506,
};

// Helper to get the model src URL from content array
export function getModelSrc(data: ModelData): string {
  const modelContent = data.content?.find(c => c.type === 'model');
  return modelContent?.path || '';
}

export const toTargetNames = (name: string | string[]): string[] =>
  Array.isArray(name) ? name : [name];

export function getModelHash(model: ModelData): string {
  return model.urlHash || model.id;
}

export function findModelByIdOrHash(idOrHash: string): ModelData | undefined {
  return ContentData.find((m) => m.id === idOrHash || m.urlHash === idOrHash);
}
